import React, { useState } from 'react';
import { getPedidos, getUsuarios } from '../../services/api';

function Informes() {
  const [tipoInforme, setTipoInforme] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [loading, setLoading] = useState(false);
  const [datos, setDatos] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  // Helper para validar y obtener fecha en formato ISO
  const getFechaISO = (fecha) => {
    if (!fecha) return null;
    const date = new Date(fecha);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  // Helper para obtener fecha localizada de forma segura
  const getFechaLocalizada = (fecha) => {
    if (!fecha) return 'Fecha inválida';
    const date = new Date(fecha);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    return date.toLocaleDateString();
  };

  const generarInforme = async () => {
    if (!tipoInforme) {
      mostrarMensaje('Selecciona un tipo de informe', 'error');
      return;
    }

    setLoading(true);
    setDatos(null);

    try {
      let resultado = null;

      switch (tipoInforme) {
        case 'pedidos_por_fecha':
          resultado = await informePedidosPorFecha();
          break;
        case 'platos_mas_pedidos':
          resultado = await informePlatosMasPedidos();
          break;
        case 'usuarios_activos':
          resultado = await informeUsuariosActivos();
          break;
        case 'resumen_diario':
          resultado = await informeResumenDiario();
          break;
        default:
          mostrarMensaje('Tipo de informe no válido', 'error');
      }

      setDatos(resultado);
    } catch (error) {
      console.error('Error generando informe:', error);
      mostrarMensaje('Error al generar el informe', 'error');
    } finally {
      setLoading(false);
    }
  };

  const informePedidosPorFecha = async () => {
    if (!fechaDesde || !fechaHasta) {
      throw new Error('Selecciona rango de fechas');
    }

    const response = await getPedidos();
    const pedidos = response.data;

    const pedidosFiltrados = pedidos.filter((p) => {
      const fechaPedido = getFechaISO(p.fecha);
      if (!fechaPedido) return false;
      return fechaPedido >= fechaDesde && fechaPedido <= fechaHasta;
    });

    return {
      tipo: 'pedidos_por_fecha',
      total: pedidosFiltrados.length,
      pedidos: pedidosFiltrados,
    };
  };

  const informePlatosMasPedidos = async () => {
    const response = await getPedidos();
    const pedidos = response.data;

    const conteoPlatos = {};

    pedidos.forEach((pedido) => {
      if (pedido.items) {
        pedido.items.forEach((item) => {
          const key = item.plato;
          if (!conteoPlatos[key]) {
            conteoPlatos[key] = {
              nombre: item.plato,
              categoria: item.categoria,
              cantidad: 0,
            };
          }
          conteoPlatos[key].cantidad++;
        });
      }
    });

    const platosOrdenados = Object.values(conteoPlatos).sort(
      (a, b) => b.cantidad - a.cantidad
    );

    return {
      tipo: 'platos_mas_pedidos',
      platos: platosOrdenados,
    };
  };

  const informeUsuariosActivos = async () => {
    const response = await getUsuarios();
    const usuarios = response.data;

    const activos = usuarios.filter((u) => u.activo);
    const inactivos = usuarios.filter((u) => !u.activo);

    const porTipo = {
      admin: usuarios.filter((u) => u.tipo_usuario === 'admin').length,
      oficina: usuarios.filter((u) => u.tipo_usuario === 'oficina').length,
      operario: usuarios.filter((u) => u.tipo_usuario === 'operario').length,
    };

    return {
      tipo: 'usuarios_activos',
      total: usuarios.length,
      activos: activos.length,
      inactivos: inactivos.length,
      porTipo,
      usuarios: activos,
    };
  };

  const informeResumenDiario = async () => {
    if (!fechaDesde) {
      throw new Error('Selecciona una fecha');
    }

    const response = await getPedidos();
    const pedidos = response.data;

    const pedidosDia = pedidos.filter((p) => {
      const fechaPedido = getFechaISO(p.fecha);
      if (!fechaPedido) return false;
      return fechaPedido === fechaDesde;
    });

    const platosPorCategoria = {};
    let totalPedidos = pedidosDia.length;

    pedidosDia.forEach((pedido) => {
      if (pedido.items) {
        pedido.items.forEach((item) => {
          const cat = item.categoria || 'Sin categoría';
          if (!platosPorCategoria[cat]) {
            platosPorCategoria[cat] = [];
          }
          platosPorCategoria[cat].push(item.plato);
        });
      }
    });

    return {
      tipo: 'resumen_diario',
      fecha: fechaDesde,
      totalPedidos,
      platosPorCategoria,
      pedidos: pedidosDia,
    };
  };

  const mostrarMensaje = (texto, tipo) => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 5000);
  };

  const imprimirInforme = () => {
    window.print();
  };

  const exportarCSV = () => {
    if (!datos) return;

    let csv = '';
    let filename = 'informe.csv';

    switch (datos.tipo) {
      case 'pedidos_por_fecha':
        csv = 'Fecha,Usuario,Estado,Platos\n';
        datos.pedidos.forEach((p) => {
          const platos = p.items?.map((pl) => pl.plato).join('; ') || '';
          csv += `${getFechaLocalizada(p.fecha)},${
            p.usuario.nombre || p.usuario.dni
          },${p.estado},"${platos}"\n`;
        });
        filename = `pedidos_${fechaDesde}_${fechaHasta}.csv`;
        break;

      case 'platos_mas_pedidos':
        csv = 'Plato,Categoría,Cantidad\n';
        datos.platos.forEach((p) => {
          csv += `"${p.nombre}","${p.categoria}",${p.cantidad}\n`;
        });
        filename = 'platos_mas_pedidos.csv';
        break;

      case 'usuarios_activos':
        csv = 'DNI,Nombre,Apellido,Tipo,Estado\n';
        datos.usuarios.forEach((u) => {
          csv += `${u.dni},"${u.nombre}","${u.apellido}",${u.tipo_usuario},${
            u.activo ? 'Activo' : 'Inactivo'
          }\n`;
        });
        filename = 'usuarios_activos.csv';
        break;

      default:
        mostrarMensaje('No se puede exportar este tipo de informe', 'error');
        return;
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  return (
    <div>
      <div className="page-header no-print">
        <h1>📊 Informes y Reportes</h1>
        <p>Genera reportes detallados del sistema</p>
      </div>

      {mensaje && (
        <div className={`alert alert-${mensaje.tipo} no-print`}>{mensaje.texto}</div>
      )}

      {/* Configuración de informe */}
      <div className="card no-print">
        <h2>Configurar Informe</h2>

        <div className="form-group">
          <label>Tipo de Informe *</label>
          <select
            className="form-control"
            value={tipoInforme}
            onChange={(e) => setTipoInforme(e.target.value)}
          >
            <option value="">Selecciona un tipo de informe</option>
            <option value="pedidos_por_fecha">📅 Pedidos por Rango de Fechas</option>
            <option value="resumen_diario">📋 Resumen Diario de Producción</option>
            <option value="platos_mas_pedidos">🍽️ Platos Más Pedidos</option>
            <option value="usuarios_activos">👥 Usuarios Activos</option>
          </select>
        </div>

        {(tipoInforme === 'pedidos_por_fecha' || tipoInforme === 'resumen_diario') && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Fecha {tipoInforme === 'pedidos_por_fecha' ? 'Desde' : ''} *</label>
              <input
                type="date"
                className="form-control"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
              />
            </div>

            {tipoInforme === 'pedidos_por_fecha' && (
              <div className="form-group">
                <label>Fecha Hasta *</label>
                <input
                  type="date"
                  className="form-control"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            className="btn btn-primary"
            onClick={generarInforme}
            disabled={loading}
          >
            {loading ? 'Generando...' : '🔍 Generar Informe'}
          </button>

          {datos && (
            <>
              <button className="btn btn-success" onClick={imprimirInforme}>
                🖨️ Imprimir
              </button>
              <button className="btn btn-secondary" onClick={exportarCSV}>
                📥 Exportar CSV
              </button>
            </>
          )}
        </div>
      </div>

      {/* Resultados */}
      {datos && (
        <div className="card">
          {/* Pedidos por Fecha */}
          {datos.tipo === 'pedidos_por_fecha' && (
            <>
              <h2>Pedidos del {fechaDesde} al {fechaHasta}</h2>
              <div className="stats-grid" style={{ marginBottom: '20px' }}>
                <div className="stat-card">
                  <h3>{datos.total}</h3>
                  <p>Total Pedidos</p>
                </div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>DNI</th>
                    <th>Estado</th>
                    <th>Platos</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.pedidos.map((pedido) => (
                    <tr key={pedido.id}>
                      <td>{getFechaLocalizada(pedido.fecha)}</td>
                      <td>{pedido.usuario?.nombre || '-'}</td>
                      <td>{pedido.usuario?.dni || '-'}</td>
                      <td>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background:
                              pedido.estado === 'confirmado' ? '#d4edda' : '#fff3cd',
                            color:
                              pedido.estado === 'confirmado' ? '#155724' : '#856404',
                          }}
                        >
                          {pedido.estado}
                        </span>
                      </td>
                      <td>
                        {pedido.items?.map((item) => item.plato).join(', ') || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Platos Más Pedidos */}
          {datos.tipo === 'platos_mas_pedidos' && (
            <>
              <h2>Platos Más Pedidos</h2>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Plato</th>
                    <th>Categoría</th>
                    <th>Cantidad de Pedidos</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.platos.map((plato, index) => (
                    <tr key={index}>
                      <td><strong>{index + 1}</strong></td>
                      <td>{plato.nombre}</td>
                      <td>{plato.categoria}</td>
                      <td>
                        <span
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            background: '#3498db',
                            color: 'white',
                            fontWeight: '600',
                          }}
                        >
                          {plato.cantidad}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Usuarios Activos */}
          {datos.tipo === 'usuarios_activos' && (
            <>
              <h2>Estadísticas de Usuarios</h2>
              <div className="stats-grid" style={{ marginBottom: '20px' }}>
                <div className="stat-card">
                  <h3>{datos.total}</h3>
                  <p>Total Usuarios</p>
                </div>
                <div className="stat-card">
                  <h3>{datos.activos}</h3>
                  <p>Activos</p>
                </div>
                <div className="stat-card">
                  <h3>{datos.porTipo.admin}</h3>
                  <p>Administradores</p>
                </div>
                <div className="stat-card">
                  <h3>{datos.porTipo.oficina}</h3>
                  <p>Oficina</p>
                </div>
                <div className="stat-card">
                  <h3>{datos.porTipo.operario}</h3>
                  <p>Operarios</p>
                </div>
              </div>

              <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>
                Usuarios Activos
              </h3>
              <table>
                <thead>
                  <tr>
                    <th>DNI</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.usuarios.map((usuario) => (
                    <tr key={usuario.id}>
                      <td>{usuario.dni}</td>
                      <td>{usuario.nombre}</td>
                      <td>{usuario.apellido}</td>
                      <td>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background:
                              usuario.tipo_usuario === 'admin'
                                ? '#e74c3c'
                                : usuario.tipo_usuario === 'oficina'
                                ? '#3498db'
                                : '#27ae60',
                            color: 'white',
                          }}
                        >
                          {usuario.tipo_usuario}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Resumen Diario */}
          {datos.tipo === 'resumen_diario' && (
            <>
              <h2>Resumen de Producción - {datos.fecha}</h2>
              <div className="stats-grid" style={{ marginBottom: '20px' }}>
                <div className="stat-card">
                  <h3>{datos.totalPedidos}</h3>
                  <p>Total Pedidos</p>
                </div>
              </div>

              <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>
                Platos por Categoría
              </h3>
              {Object.entries(datos.platosPorCategoria).map(([categoria, platos]) => (
                <div key={categoria} style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>
                    {categoria} ({platos.length} porciones)
                  </h4>
                  <div
                    style={{
                      background: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '8px',
                    }}
                  >
                    {platos.map((plato, idx) => (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-block',
                          margin: '5px',
                          padding: '6px 12px',
                          background: 'white',
                          borderRadius: '6px',
                          fontSize: '14px',
                        }}
                      >
                        {plato}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Informes;
